import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import type { Tone } from './Badge';

/*
  The figure tiles across the top of a board.

  This used to live inside DashboardPage as a local `StatCard` whose `colour`
  prop took a raw Tailwind class string — `"bg-green-100 text-green-600"` —
  chosen at each call site. So the tiles could not be reused anywhere else, and
  nothing stopped two boards giving the same quantity two different colours.

  The tone is now semantic. A tile is tinted only where the tint MEANS
  something: `danger` on failed attempts, `success` on released. A plain count
  gets no tint at all, because colouring every tile spends the reader's
  attention on all four equally and so on none of them.
*/

const ICON_TONES: Record<Tone, string> = {
  success: 'bg-success/[0.09] text-[hsl(var(--record-ink-success))] border-success/25',
  warning: 'bg-warning/[0.11] text-[hsl(var(--record-ink-warning))] border-warning/30',
  danger: 'bg-destructive/[0.08] text-[hsl(var(--record-ink-danger))] border-destructive/25',
  info: 'bg-info/[0.09] text-[hsl(var(--record-ink-info))] border-info/25',
  neutral: 'bg-muted text-muted-foreground border-border',
};

export interface StatTileProps {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  /** A denominator or qualifier — "of 412 enrolled", "since 07:00". */
  hint?: string;
  /** Makes the whole tile a filter toggle for the table below it. */
  onClick?: () => void;
  active?: boolean;
  className?: string;
}

export function StatTile({
  label,
  value,
  icon,
  tone = 'neutral',
  hint,
  onClick,
  active,
  className,
}: StatTileProps) {
  const interactive = typeof onClick === 'function';

  return (
    <div
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      aria-pressed={interactive ? !!active : undefined}
      className={cn(
        styles.panel,
        'flex items-center gap-3 px-3.5 py-3',
        interactive &&
          'cursor-pointer transition-colors hover:bg-muted focus-visible:outline-none ' +
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ' +
            'focus-visible:ring-offset-background',
        /* The active filter is marked with a left rule rather than a fill, so
           the figure keeps the same ground as its siblings and stays
           comparable with them. */
        active && 'border-l-2 border-l-primary bg-accent/50',
        className,
      )}
    >
      {icon && (
        <div
          className={cn(
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border',
            ICON_TONES[tone],
          )}
          aria-hidden
        >
          {icon}
        </div>
      )}
      <div className="min-w-0">
        <p className="text-[21px] font-bold leading-none tracking-tight tabular-nums text-foreground">
          {value}
        </p>
        <p className={cn(styles.label, 'mt-1.5 truncate')}>{label}</p>
        {hint && <p className="mt-0.5 truncate text-[10px] text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}
